import React, { useState, useEffect, useRef, useMemo } from 'react'
import { motion, HTMLMotionProps } from 'framer-motion'

const styles = {
    wrapper: {
        display: 'inline-block',
    },
    srOnly: {
        position: 'absolute' as const,
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden' as const,
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap' as const,
        border: '0',
    },
}

interface DecryptedTextProps extends Omit<HTMLMotionProps<'span'>, 'ref'> {
    text: string
    speed?: number
    maxIterations?: number
    sequential?: boolean
    revealDirection?: 'start' | 'end' | 'center'
    useOriginalCharsOnly?: boolean
    characters?: string
    className?: string
    parentClassName?: string
    encryptedClassName?: string
    animateOn?: 'view' | 'hover'
}

export default function DecryptedText({
    text,
    speed = 50,
    maxIterations = 10,
    sequential = false,
    revealDirection = 'start',
    useOriginalCharsOnly = false,
    characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+',
    className = '',
    parentClassName = '',
    encryptedClassName = '',
    animateOn = 'hover',
    ...props
}: DecryptedTextProps) {
    const [displayText, setDisplayText] = useState(text)
    const [isScrambling, setIsScrambling] = useState(false)
    const [isHovering, setIsHovering] = useState(false)
    const [hasBeenViewed, setHasBeenViewed] = useState(false)
    const [revealedIndices, setRevealedIndices] = useState<Set<number>>(new Set())
    const [isInView, setIsInView] = useState(false)
    
    const containerRef = useRef<HTMLSpanElement>(null)

    const charactersArray = useMemo(() => {
        return useOriginalCharsOnly 
            ? Array.from(new Set(text.split(''))).filter(char => char !== ' ')
            : characters.split('')
    }, [text, useOriginalCharsOnly, characters])

    const shouldAnimate = animateOn === 'hover' ? isHovering : hasBeenViewed

    const animate = () => {
        if (isScrambling) return

        setIsScrambling(true)
        setRevealedIndices(new Set())
        
        const totalDuration = text.length * speed + maxIterations * speed
        let elapsedTime = 0
        
        const getNextIndex = (revealedSet: Set<number>): number => {
            const availableIndices = text
                .split('')
                .map((_, index) => index)
                .filter(index => !revealedSet.has(index) && text[index] !== ' ')
            
            if (availableIndices.length === 0) return -1
            
            if (sequential) {
                switch (revealDirection) {
                    case 'end':
                        return availableIndices[availableIndices.length - 1]
                    case 'center':
                        const midIndex = Math.floor(availableIndices.length / 2)
                        return availableIndices[midIndex]
                    default:
                        return availableIndices[0]
                }
            } else {
                return availableIndices[Math.floor(Math.random() * availableIndices.length)]
            }
        }

        const shuffleText = (originalText: string, currentRevealed: Set<number>): string => {
            return originalText
                .split('')
                .map((char, index) => {
                    if (char === ' ' || currentRevealed.has(index)) {
                        return char
                    }
                    return charactersArray[Math.floor(Math.random() * charactersArray.length)]
                })
                .join('')
        }

        const interval = setInterval(() => {
            elapsedTime += speed

            setRevealedIndices(currentRevealed => {
                const newRevealed = new Set(currentRevealed)
                
                const shouldRevealNext = 
                    elapsedTime >= maxIterations * speed && 
                    newRevealed.size < text.replace(/\s/g, '').length

                if (shouldRevealNext) {
                    const nextIndex = getNextIndex(newRevealed)
                    if (nextIndex !== -1) {
                        newRevealed.add(nextIndex)
                    }
                }

                return newRevealed
            })

            setDisplayText(shuffleText(text, revealedIndices))

            const allNonSpaceIndices = text
                .split('')
                .map((_, index) => index)
                .filter(index => text[index] !== ' ')
            
            const allRevealed = allNonSpaceIndices.every(index => revealedIndices.has(index))
            
            if (elapsedTime >= totalDuration || allRevealed) {
                clearInterval(interval)
                setDisplayText(text)
                setIsScrambling(false)
                setRevealedIndices(new Set(allNonSpaceIndices))
            }
        }, speed)

        return () => clearInterval(interval)
    }

    useEffect(() => {
        if (shouldAnimate) {
            animate()
        } else {
            setDisplayText(text)
            setIsScrambling(false)
            setRevealedIndices(new Set())
        }
    }, [shouldAnimate, text])

    useEffect(() => {
        if (animateOn === 'view' && containerRef.current) {
            const observerCallback = (entries: IntersectionObserverEntry[]) => {
                const [entry] = entries
                setIsInView(entry.isIntersecting)
                
                if (entry.isIntersecting && !hasBeenViewed) {
                    setHasBeenViewed(true)
                }
            }

            const observer = new IntersectionObserver(observerCallback, {
                threshold: 0.1,
                rootMargin: '50px'
            })

            observer.observe(containerRef.current)

            return () => observer.disconnect()
        }
    }, [animateOn, hasBeenViewed])

    const hoverProps = animateOn === 'hover' ? {
        onMouseEnter: () => setIsHovering(true),
        onMouseLeave: () => setIsHovering(false),
    } : {}

    return (
        <motion.span 
            ref={containerRef} 
            style={styles.wrapper} 
            className={parentClassName}
            {...hoverProps} 
            {...props}
        >
            <span style={styles.srOnly}>{displayText}</span>

            <span aria-hidden="true">
                {displayText.split('').map((char, index) => {
                    const isRevealedOrDone =
                        revealedIndices.has(index) || !isScrambling || !isHovering

                    return (
                        <span
                            key={index}
                            className={isRevealedOrDone ? className : encryptedClassName}
                        >
                            {char}
                        </span>
                    )
                })}
            </span>
        </motion.span>
    )
} 